// /$lang/tour/$slug — per-language, indexable tour pages.
//
// - Validates $lang against SUPPORTED_LANGS; invalid languages 404.
// - Spanish (default) 301-redirects to /tour/$slug so es has one canonical URL.
// - Overrides router context.lang and i18next so SSR renders the target
//   language on the first paint (no client-only flash).
// - Emits self-referential canonical + full hreflang set via buildTourHead.
import { pickOgPhoto, tidyText } from "@/lib/og";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { getUnifiedTour, parseUnifiedSlug } from "@/lib/catalog.functions";
import { getPresentationPrices } from "@/lib/presentation-prices.functions";
import { getApprovedReviews } from "@/lib/verified-reviews.functions";
import { cleanTourTitle, metaDescription } from "@/lib/presentation-text";
import { queryOptions } from "@tanstack/react-query";
import { stripHtml } from "@/lib/slug";
import { buildTourHead, toAbsoluteUrl, DEFAULT_TOUR_LANG } from "@/lib/tour-seo";
import { SUPPORTED_LANGS, type AppLang, setLanguage } from "@/i18n";
import i18n from "@/i18n";
import { TourDetailShell, TourNotFoundView } from "@/components/tour/TourDetailShell";
import { isRetiredTour, RETIRED_REDIRECT_CITY } from "@/lib/retired-tours";
import { pinnedSlugRedirect } from "@/lib/canonical-tour-slugs";


const SUPPORTED = new Set<string>(SUPPORTED_LANGS as readonly string[]);

const tourQueryLocalized = (source: "bokun" | "custom", key: string, lang: string) =>
  queryOptions({
    queryKey: ["catalog", "tour", source, key, lang],
    queryFn: () => getUnifiedTour({ data: { source, idOrSlug: key, lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/$lang/tour/$slug")({
  beforeLoad: async ({ params, search }) => {
    const lang = (params.lang ?? "").toLowerCase();
    if (!SUPPORTED.has(lang)) throw notFound();
    const pinned = pinnedSlugRedirect(params.slug);
    if (lang === DEFAULT_TOUR_LANG) {
      // Single hop straight to the canonical Spanish URL.
      throw redirect({
        to: "/tour/$slug",
        params: { slug: pinned ?? params.slug },
        search: search as never,
        statusCode: 301,
      });
    }
    if (pinned) {
      throw redirect({
        to: "/$lang/tour/$slug",
        params: { lang, slug: pinned },
        search: search as never,
        statusCode: 301,
      });
    }
    if (isRetiredTour(params.slug)) {
      throw redirect({
        to: "/tours/$city",
        params: { city: RETIRED_REDIRECT_CITY },
        statusCode: 301,
      });
    }

    // Sync i18next so SSR renders in the URL's language.
    if (i18n.language !== lang) {
      try {
        await setLanguage(lang);
      } catch {
        /* non-fatal */
      }
    }
    return { lang: lang as AppLang };
  },
  loader: async ({ params, context }) => {
    const lang = params.lang as AppLang;
    const { source, key } = parseUnifiedSlug(params.slug);
    const result = await context.queryClient.ensureQueryData(
      tourQueryLocalized(source, key, lang),
    );
    if (!result.tour) throw notFound();
    const t = result.tour;
    const short = stripHtml(t.summary || "").trim();
    const description = metaDescription(/[.!?]$/.test(short) ? short : t.description || short);
    const image = toAbsoluteUrl(pickOgPhoto(t.images));
    const prices = source === "bokun" ? await getPresentationPrices({data:{ids:[key]}}) : {};
    const finalPrice = prices[key] ?? null;
    if (source === "bokun") context.queryClient.setQueryData(["tour-card-price",key],finalPrice);
    const rating = source === "bokun" ? await getApprovedReviews({data:{productId:key}}) : { avg:0,count:0,reviews:[] };
    if (source === "bokun") context.queryClient.setQueryData(["verified-reviews",key],rating);
    return {
      source,
      key,
      title: cleanTourTitle(t.title),
      description,
      image,
      cardImage: t.images?.[0],
      place: t.location ?? null,
      priceFrom: finalPrice?.amount ?? null,
      prices,
      reviews: rating.reviews,
      currency: finalPrice?.currency ?? "MXN",
      ratingAvg: rating.avg,
      ratingCount: rating.count,
    };

  },
  head: ({ loaderData, params }) =>
    buildTourHead({
      loaderData,
      slug: params.slug,
      lang: (params.lang as AppLang) ?? DEFAULT_TOUR_LANG,
    }),
  component: LangTourDetailPage,
  notFoundComponent: TourNotFoundView,
  errorComponent: ({ error }) => (
    <div className="p-12 text-center text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>
  ),
});

function LangTourDetailPage() {
  const { source, key, prices } = Route.useLoaderData();
  const { slug, lang } = Route.useParams();
  return <TourDetailShell source={source} idKey={key} slug={slug} presentationPrices={prices} languageOverride={lang} />;
}

